import { gql } from '@apollo/client';
import { BASIC_TASK_TYPE_FIELDS_FRAGMENT, TASK_WITH_RESOURCED_FIELDS_FRAGMENT } from './task';
import { USER_FIELDS_FRAGMENT, USER_PROJECT_ROLE_TYPE_FRAGMENT } from './user';
import { FR_INSPECTION_LIST_NAME_ID_FIELDS } from './inspection';
import { MACHINE_SIMPLE_FIELDS } from './fleet';
import { FR_RESOURCE_ASSET_FIELDS } from './resourceAsset';

export const PLANNING_PROJECT_LESS_FIELDS_FRAGMENT = gql`
    fragment PlanningProjectLessFields on Project {
        Name
        ProjectId
        ProjectNbr
        Creator
        Orderer
        Created
        Updated
        StartDate
        EndDate
        Target
        WorkPackage {
            WorkPackageId
            WorkNbr
            WorkName
            WorkPackageStatus {
                WorkPackageStatusId
                Name
            }
        }
        ProjectType {
            ProjectTypeId
            Name
            Created
            Updated
        }
        ProjectStatus {
            ProjectStatusId
            Name
        }
        ProjectSummary {
            TaskSummary {
                TaskType {
                    TaskTypeId
                    Name
                    Abbr
                    TaskGroup
                }
                TaskStatus {
                    TaskStatusId
                    Name
                }
                Open
                Assigned
                Resourced
                Ongoing
                Done
                Cancelled
                Failed
                Paused
                Total
            }
            TaskDone
            TaskTotal
        }
    }
`;

export const PROJECT_LESS_FIELDS_FRAGMENT = gql`
    fragment ProjectLessFields on Project {
        Name
        ProjectId
        ProjectNbr
        Creator
        Orderer
        Created
        Updated
        StartDate
        EndDate
        Target
        PossibleStartDate
        CableShowingDate
        Addr
        ProjectType {
            ProjectTypeId
            Name
            Created
            Updated
        }
        Access {
            Read
            Create
            Delete
            Update
        }
        Tasks {
            ...TaskResourcedFields
        }
        ProjectStatus {
            ProjectStatusId
            Name
        }
        ProjectUsers {
            User {
                UserState
                Firstname
                Lastname
                Phone
                Email
                UserId
                OwnOrganization {
                    OrganizationId
                    Name
                }
            }
            AccessUserType {
                ... on UserProjectRoleType {
                    UserProjectRoleTypeId
                    Name
                }
            }
        }
        ProjectSummary {
            TaskSummary {
                TaskType {
                    TaskTypeId
                    Name
                    Abbr
                    TaskGroup
                }
                TaskStatus {
                    TaskStatusId
                    Name
                }
                Open
                Assigned
                Resourced
                Ongoing
                Done
                Cancelled
                Failed
                Paused
                Total
            }
            TaskDone
            TaskTotal
        }
    }
    ${TASK_WITH_RESOURCED_FIELDS_FRAGMENT}
`;

export const PROJECT_FIELDS_FRAGMENT = gql`
    fragment ProjectFields on Project {
        ...ProjectLessFields
        Epic {
            EpicId
            Name
            EpicNbr
            DataOwner
            Secret
            CoordinateSystemType {
                CoordinateSystemTypeId
                Name
                DatumCode
            }
            HeightSystemType {
                HeightSystemTypeId
                Name
            }
            Access {
                Read
                Create
                Delete
                Update
            }
        }
        ProjectInspection {
            ProjectInspectionId
            InspectionList {
                ...fr_InspectionListNameIdFields
            }
        }
        InspectionEntries {
            InspectionEntryId
            InspectionList {
                InspectionListId
            }
            InspectionEntryStatus {
                Name
            }
        }
        Machines {
            ...MachineSimpleInfoFields
        }
        ResourceAssets {
            ...fr_ResourceAssetFields
        }
        WorkPackage {
            WorkPackageId
            WorkName
            WorkNbr
            Access {
                Read
                Create
                Delete
                Update
            }
            DataSource {
                WorkPackageDataSourceId
                Token
                Url
            }
            Cables
            Pipes
            Lines
            LocationMoveAllowedAtObstacles
            MaxLocationMove
            AgreeLocationMoveWithDesigner
            TaskTypeInstruction {
                TaskTypeInstructionId
                WorkPackage {
                    WorkPackageId
                }
                Instruction
                TaskType {
                    ...BasicTaskTypeFields
                }
            }
        }
    }
    ${PROJECT_LESS_FIELDS_FRAGMENT}
    ${BASIC_TASK_TYPE_FIELDS_FRAGMENT}
    ${FR_INSPECTION_LIST_NAME_ID_FIELDS}
    ${MACHINE_SIMPLE_FIELDS}
    ${FR_RESOURCE_ASSET_FIELDS}
`;

export const PROJECT_WITH_TASKS_FRAGMENT = gql`
    fragment ProjectWithTasks on Project {
        ...ProjectFields
        Tasks {
            ...TaskResourcedFields
        }
    }
    ${PROJECT_FIELDS_FRAGMENT}
    ${TASK_WITH_RESOURCED_FIELDS_FRAGMENT}
`;

export const PROJECT_USERS_FRAGMENT = gql`
    fragment ProjectUsers on Project {
        ProjectId
        ProjectUsers {
            User {
                ...UserFields
            }
            AccessUserType {
                __typename
                ... on UserProjectRoleType {
                    ...UserProjectRoleTypeFields
                }
            }
        }
    }
    ${USER_FIELDS_FRAGMENT}
    ${USER_PROJECT_ROLE_TYPE_FRAGMENT}
`;

export const SIMPLE_PROJECT_FIELDS_FRAGMENT = gql`
    fragment SimpleProjectFields on Project {
        ProjectId
        Name
        ProjectNbr
        Access {
            Read
            Create
            Delete
            Update
        }
        Created
        Updated
    }
`;
